import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { IOrientationGame } from './orientation-game.interface';
import {
  IDeleteParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from '../../common/common.interface';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { Observable, map, tap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'orientation-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IOrientationGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class OrientationGameCoreService {
  private _loading = false;

  /**
   * @name orientationGameList$
   * @description emits stored list of the orientation Games
   */

  orientationGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getOrientationGames(): void {
    this._loading = true;
    this._api
      .get('master-game/list', { type: 'orientation' }, {})
      .subscribe((data: IOrientationGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addOrientationGame(dataInput): Observable<any> {
    this._loading = true;
    const data = { ...dataInput, masterGameType: 'orientation' };
    return this._api.post('master-game/add', data, {}).pipe(
      tap((data: IOrientationGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateOrientationGame(dataInput): Observable<any> {
    this._loading = true;
    const data = { ...dataInput, masterGameType: 'orientation' };
    return this._api.put('master-game/edit', data, {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IOrientationGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteOrientationGame(data: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('master-game', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading = false;
      }),
    );
  }

  public getOrientationGame(gameId: string): Observable<IOrientationGame> {
    return this._api.get(`master-game/${gameId}`);
  }

  public getOrientationGameInfo(id: string): IOrientationGame {
    const orientationGame = store.query(getEntity(id));
    return orientationGame;
  }

  public resolveOrientationGameId(
    orientationGame: string | IOrientationGame,
  ): string {
    let orientationGameId: string | IOrientationGame = '';
    if (orientationGame) {
      orientationGameId = orientationGame;
    }
    if (orientationGame['_id']) {
      orientationGameId = orientationGame['_id'];
    }
    return orientationGameId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getOrientationGames();
    }
  }
}

interface IOrientationGameListResponse {
  list: IOrientationGame[];
  totalCount: number;
}
